import { BASIS_POINT_DIVISOR } from './constants';

/**
 * All monetary values in the system are integers in the currency's minor unit
 * ("cents"). This module is the only place that converts to and from the
 * human-readable representation.
 */

/** Multiply a cents amount by a basis-point rate, rounding half away from zero. */
export function applyRate(amountCents: number, rateBasisPoints: number): number {
  assertInteger(amountCents, 'amountCents');
  assertInteger(rateBasisPoints, 'rateBasisPoints');
  const product = amountCents * rateBasisPoints;
  const sign = product < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(product) / BASIS_POINT_DIVISOR);
}

/** 800 -> "8" ; 1050 -> "10.5" */
export function formatRate(rateBasisPoints: number): string {
  const pct = rateBasisPoints / 100;
  return Number.isInteger(pct) ? String(pct) : String(Number(pct.toFixed(2)));
}

/** 2500 -> "25.00" (or "25" when showDecimals is false, e.g. for VND) */
export function formatAmount(amountCents: number, showDecimals = true): string {
  const negative = amountCents < 0;
  const abs = Math.abs(Math.trunc(amountCents));
  const whole = Math.floor(abs / 100);
  const frac = abs % 100;
  const body = showDecimals
    ? `${whole.toLocaleString('en-US')}.${String(frac).padStart(2, '0')}`
    : Math.round(abs / 100).toLocaleString('en-US');
  return negative ? `-${body}` : body;
}

/** 2500 -> "$25.00" (or "$25" when showDecimals is false) */
export function formatMoney(amountCents: number, symbol = '$', showDecimals = true): string {
  const negative = amountCents < 0;
  return `${negative ? '-' : ''}${symbol}${formatAmount(Math.abs(amountCents), showDecimals)}`;
}

/**
 * "25.00" | "25" | "1,234.50" | 25 -> cents. Throws on anything that is not a
 * clean amount. Grouping separators are accepted because the inputs we parse are
 * prefilled with `formatAmount`, which writes them.
 */
export function parseAmountToCents(input: string | number): number {
  const raw = typeof input === 'number' ? input.toFixed(2) : input.trim();
  if (!/^-?(\d{1,3}(,\d{3})*|\d+)(\.\d{1,2})?$/.test(raw)) {
    throw new Error(`Invalid monetary amount: "${input}"`);
  }
  const negative = raw.startsWith('-');
  const [whole, frac = ''] = raw.replace('-', '').replace(/,/g, '').split('.');
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  return negative ? -cents : cents;
}

function assertInteger(value: number, name: string): void {
  if (!Number.isInteger(value)) {
    throw new Error(`${name} must be an integer number of minor units, received ${value}`);
  }
}
